import { BuildingOfficeIcon, DevicePhoneMobileIcon } from "@heroicons/react/24/outline"
import { Circle, GoogleMap, InfoWindow, LoadScript, Marker } from "@react-google-maps/api"
import React, { useState } from "react"

import { formatPhone } from "../../utils/utils"
const travelDistanceInMeters = (distanceInMiles) => Number(distanceInMiles) * 1609.34

const MILES_PER_DEGREE = 69.17
// The 0.9 comes from estimating the half the size of the icon we're using to hide address at map zoom level 12.
// This will not work properly if you change the map zoom level!
const HIDE_LOCATION_ICON_ADJUSTMENT = 0.9 / MILES_PER_DEGREE

const Map = ({ practice, theme, ClipPath, ClipPathTwo }) => {
  const [infoWindowLocation, setInfoWindowLocation] = useState(null)

  const physicalLocations = practice.locations.filter((location) => {
    // If no kind is specified, assume it's a physical location if it has coordinates
    if (!location.kind || location.kind === "") {
      return location.latitude && location.longitude
    }
    // Otherwise check for known physical location kinds
    return location.kind === "office" || location.kind === "home" || location.kind === "physical"
  })

  if (physicalLocations.length === 0) {
    return null
  }

  let mapCenter, zoom, latDelta, longDelta
  const travelDistance = physicalLocations.find((location) => location.kind === "home")?.travelDistance || 0

  if (physicalLocations.length === 1) {
    const location = physicalLocations[0]
    mapCenter = { lat: location.latitude, lng: location.longitude }
    latDelta = location.latitude - location.latitude + travelDistance / MILES_PER_DEGREE
    longDelta = location.longitude - location.longitude + travelDistance / MILES_PER_DEGREE
    if (location.hideAddress && location.kind !== "home") {
      // northern hemisphere
      if (location.latitude > 0) {
        mapCenter.lat = mapCenter.lat + HIDE_LOCATION_ICON_ADJUSTMENT
      } else {
        mapCenter.lat = mapCenter.lat + HIDE_LOCATION_ICON_ADJUSTMENT
      }
    }
  } else {
    const latitudes = physicalLocations.map((location) => location.latitude).filter((lat) => lat !== null)
    const longitudes = physicalLocations.map((location) => location.longitude).filter((lng) => lng !== null)
    const minLatitude = Math.min(...latitudes)
    const maxLatitude = Math.max(...latitudes)
    const minLongitude = Math.min(...longitudes)
    const maxLongitude = Math.max(...longitudes)
    mapCenter = {
      lat: (minLatitude + maxLatitude) / 2,
      lng: (minLongitude + maxLongitude) / 2
    }
    latDelta = maxLatitude - minLatitude + travelDistance / MILES_PER_DEGREE
    longDelta = maxLongitude - minLongitude + travelDistance / MILES_PER_DEGREE
  }

  const latZoom = Math.floor(Math.log2(360 / latDelta))
  const longZoom = Math.floor(Math.log2(360 / longDelta))

  if (latDelta === 0 || longDelta === 0) {
    zoom = physicalLocations.some((location) => location.hideAddress) ? 12 : 11
  } else {
    zoom = Math.min(latZoom, longZoom)
    zoom = Math.max(zoom, 5)
  }

  return (
    <div className="relative w-full">
      <LoadScript googleMapsApiKey={process.env.GOOGLE_MAPS_API_KEY}>
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "420px" }}
          center={mapCenter}
          zoom={zoom}
          options={{ disableDefaultUI: true }}>
          {physicalLocations.map((location) => {
            const markerPosition = { lat: location?.latitude, lng: location?.longitude }

            return (
              <React.Fragment key={location.id}>
                {location.kind === "home" ? (
                  <Circle
                    center={markerPosition}
                    radius={travelDistanceInMeters(location.travelDistance)}
                    options={{ strokeColor: "#0D9DA4" }}
                  />
                ) : location.hideAddress ? (
                  <Marker position={markerPosition} icon="/defaults/m5.png" />
                ) : (
                  <Marker position={markerPosition} onClick={() => setInfoWindowLocation(location)} />
                )}
                {infoWindowLocation === location && (
                  <InfoWindow position={markerPosition} onCloseClick={() => setInfoWindowLocation(null)}>
                    <div className="flex flex-col gap-4 p-4 pt-0 text-[12px] text-gray-dark">
                      <div className="flex items-center gap-2">
                        <BuildingOfficeIcon className="h-4 w-4" />
                        <span>{location.address}</span>
                      </div>
                      {practice.user.phone && (
                        <div className="flex items-center gap-2">
                          <DevicePhoneMobileIcon className="h-4 w-4" />
                          <span>{formatPhone(practice.user.phone)}</span>
                        </div>
                      )}
                      <a
                        className="w-fit rounded border border-[var(--bg-color)] px-6 py-2 text-center"
                        style={{
                          color: theme.colorTextLight,
                          backgroundColor: theme.colorAccent,
                          "--bg-color": theme.colorAccent
                        }}
                        href={`https://maps.google.com/maps?daddr=${location.latitude},${location.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer">
                        Get directions
                      </a>
                    </div>
                  </InfoWindow>
                )}
              </React.Fragment>
            )
          })}
        </GoogleMap>
      </LoadScript>
      {ClipPath && <ClipPath />}
      {ClipPathTwo && <ClipPathTwo />}
    </div>
  )
}

export default Map
